import React, { createContext, useContext, useReducer } from "react";
import { Coordinates } from "types/Coordinates";

interface NavigationState {
	currentLocation: Coordinates | null;
	destination: Coordinates | null;
	isNavigating: boolean;
	route: any | null;
}

type NavigationAction =
	| { type: "SET_CURRENT_LOCATION"; payload: Coordinates }
	| { type: "SET_DESTINATION"; payload: Coordinates }
	| { type: "START_NAVIGATION" }
	| { type: "STOP_NAVIGATION" }
	| { type: "SET_ROUTE"; payload: any };

const initialState: NavigationState = {
	currentLocation: null,
	destination: null,
	isNavigating: false,
	route: null,
};

const NavigationContext = createContext<
	| {
			state: NavigationState;
			setCurrentLocation: (location: Coordinates) => void;
			setDestination: (location: Coordinates) => void;
			startNavigation: () => void;
			stopNavigation: () => void;
			setRoute: (route: any) => void;
	  }
	| undefined
>(undefined);

function navigationReducer(
	state: NavigationState,
	action: NavigationAction
): NavigationState {
	switch (action.type) {
		case "SET_CURRENT_LOCATION":
			return { ...state, currentLocation: action.payload };
		case "SET_DESTINATION":
			return { ...state, destination: action.payload };
		case "START_NAVIGATION":
			return { ...state, isNavigating: true };
		case "STOP_NAVIGATION":
			return { ...state, isNavigating: false, route: null };
		case "SET_ROUTE":
			return { ...state, route: action.payload };
		default:
			return state;
	}
}

export function NavigationProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [state, dispatch] = useReducer(navigationReducer, initialState);

	const setCurrentLocation = (location: Coordinates) => {
		dispatch({ type: "SET_CURRENT_LOCATION", payload: location });
	};

	const setDestination = (location: Coordinates) => {
		dispatch({ type: "SET_DESTINATION", payload: location });
	};

	const startNavigation = () => {
		dispatch({ type: "START_NAVIGATION" });
	};

	const stopNavigation = () => {
		dispatch({ type: "STOP_NAVIGATION" });
	};

	const setRoute = (route: any) => {
		dispatch({ type: "SET_ROUTE", payload: route });
	};

	return (
		<NavigationContext.Provider
			value={{
				state,
				setCurrentLocation,
				setDestination,
				startNavigation,
				stopNavigation,
				setRoute,
			}}
		>
			{children}
		</NavigationContext.Provider>
	);
}

export function useNavigation() {
	const context = useContext(NavigationContext);
	if (context === undefined) {
		throw new Error("useNavigation must be used within a NavigationProvider");
	}
	return context;
}
